import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react"
import { useSnackbar } from 'notistack';
import InputMask from 'react-input-mask';
import axios from "../../api/axios";
import { formatMoney } from "../../utils/helpers";
import { DateTime } from 'luxon';

const FIELD_CONFIG = [
    { id: 'BPAttendeeFN',       label: 'First Name',      placeholder: 'Enter First Name',       type: 'text',  required: true  },
    { id: 'BPAttendeeLN',       label: 'Last Name',        placeholder: 'Enter Last Name',        type: 'text',  required: true  },
    { id: 'BPAttendeeEmail',    label: 'Email',            placeholder: 'Enter Email',            type: 'email', required: true  },
    { id: 'BPAttendeePhone',    label: 'Phone',            placeholder: '(123) 456-7890',         type: 'tel',   required: true  },
    { id: 'BPAttendeeChurch',   label: 'Church',           placeholder: 'Enter Church Name',      type: 'text',  required: true  },
    { id: 'BPAttendeeAddress',  label: 'Address',          placeholder: 'Enter Address',          type: 'text',  required: true  },
    { id: 'BPAttendeeAddress2', label: 'Address Line 2',   placeholder: 'Enter Address Line 2',   type: 'text',  required: false },
    { id: 'BPAttendeeCity',     label: 'City',             placeholder: 'Enter City',             type: 'text',  required: true  },
    { id: 'BPAttendeeState',    label: 'State',            placeholder: 'Enter State',            type: 'text',  required: true  },
    { id: 'BPAttendeeZip',      label: 'Zip Code',         placeholder: 'Enter Zip Code',         type: 'text',  required: true  },
];

 //datetime helper
  const toISOSafe = (val) => {
  if (!val) return null;

  // True Luxon DateTime
  if (DateTime.isDateTime(val)) return val.toISODate();

  // Plain JS Date
  if (val instanceof Date) return DateTime.fromJSDate(val).toISODate();

  // Already an ISO string — slice to date portion only
  if (typeof val === 'string') return val.slice(0, 10);

  return null;
};

const AttendeeForm = forwardRef((props, ref) => {
    const { enqueueSnackbar } = useSnackbar();
    const registration = props.Registration;
    const usr = props.User;
    const team = props.Team;
    const event = props.Event;
    const quantity = parseInt(props.Quantity) > 0 ? parseInt(props.Quantity) : 1;
    // Keep a ref that always reflects the latest attendeeDates prop
      const attendeeDatesRef = useRef(props.AttendeeDates || { arrivalDate: null, departureDate: null });
    const formIndex = props.index ?? 0;
    const globalIndex = props.globalIndex ?? 0;
    const onReadyChange = props.onReadyChange;
    // Insurance is calculated ONCE, centrally, in Register.js (it needs to
    // stay in lockstep with the order-level insurance total there). This
    // component just consumes the finished per-person rate for its own
    // BPAttendeeBalance — it no longer fetches the insurance option or
    // recomputes the day-based rate itself.
    const insuranceCost = props.InsuranceCost ?? 0;

     // Read from ref so we always get the latest value, not the stale closure value
    const currentDates = attendeeDatesRef.current;

    const [attendee, setAttendee] = useState({
        BPEventID: parseInt(event.EventsID),
        BPAttendeeUserID: parseInt(usr?.UserID),
        BPAttendeeFN: '',
        BPAttendeeLN: '',
        BPAttendeeEmail: '',
        BPAttendeePhone: '',
        BPAttendeeAddress: '',
        BPAttendeeAddress2: '',
        BPAttendeeCity: '',
        BPAttendeeState: '',
        BPAttendeeZip: '',
        BPAttendeeChurch: '',
        BPAttendeeLabel: registration.SectionLabel,
        BPAttendeeRegistrationDate: DateTime.now().toString(),
        BPRegistrationID: parseInt(registration.RegistrationID),
        BPAttendeeType: registration.RegistrationName,
        BPAttendeeBalance: parseFloat(registration.RegistrationCost),
        BPAttendeeStatus: registration.RequireApproval ? "Pending" : "Approved",
        BPAttendeeApproved: registration.RequireApproval ? false : true,
        ...(team && { BPAttendeeGroup: team.BPGroupName }),
        ...(event.BP_DatesFlexible && {
             BPAttendeeArrivalDate: toISOSafe(currentDates.arrivalDate),
             BPAttendeeDepartureDate: toISOSafe(currentDates.departureDate),
        }),
    });

    // Track which fields the user has interacted with so we don't show
    // "is-invalid" styling on first render.
    const [touched, setTouched] = useState({});
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const activeFields = typeof registration.RegistrationFields === 'string'
        ? registration.RegistrationFields.split(',').map(f => f.trim()).filter(Boolean)
        : (Array.isArray(registration.RegistrationFields) ? registration.RegistrationFields : []);

    const allFieldsFilled = activeFields.every(field => {
    const config = FIELD_CONFIG.find(f => f.id === field);
    if (!config?.required) return true; // skip optional fields
    const val = attendee[field];
    return val !== undefined && val !== null && String(val).trim() !== '';
});

    const canSubmit = allFieldsFilled;

    useEffect(() => {
        onReadyChange?.(globalIndex, canSubmit);
    }, [canSubmit, globalIndex, onReadyChange]);

     useEffect(() => {
        attendeeDatesRef.current = props.AttendeeDates || { arrivalDate: null, departureDate: null };
        //console.log('AttendeeDates updated:', attendeeDatesRef.current);
  }, [props.AttendeeDates]);

  // Add this after the existing attendeeDatesRef useEffect
useEffect(() => {
    const dates = props.AttendeeDates || {};
    if (!event.BP_DatesFlexible) return;

    setAttendee(prev => ({
        ...prev,
        BPAttendeeArrivalDate: toISOSafe(dates.arrivalDate) ?? prev.BPAttendeeArrivalDate,
        BPAttendeeDepartureDate: toISOSafe(dates.departureDate) ?? prev.BPAttendeeDepartureDate,
    }));
}, [props.AttendeeDates]);

    function handleAttendeeInputChange(e) {
        const { id, value } = e.target;
        setAttendee(prev => ({ ...prev, [id]: value }));
    }

    function handleBlur(fieldId) {
        setTouched(prev => ({ ...prev, [fieldId]: true }));
    }

    // ── submit() now ONLY creates the attendee record ─────────────────────────
   // In submit(), override the balance before posting:
async function submit(groupOverride) {
    setSubmitAttempted(true);
    if (!canSubmit) {
        enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
        return null;
    }
    const currentDates = attendeeDatesRef.current;
    try {
        const payload = {
            ...attendee,
            BPAttendeeBalance: parseFloat(registration.RegistrationCost) + insuranceCost,
            ...(event.BP_DatesFlexible && {
                BPAttendeeArrivalDate: toISOSafe(currentDates.arrivalDate),
                BPAttendeeDepartureDate: toISOSafe(currentDates.departureDate),
            }),
            // Use whatever group Register.js resolved for THIS submission
            // (existing or newly-created), not the stale `team` prop this
            // form mounted with.
            ...(groupOverride && { BPAttendeeGroup: groupOverride.BPGroupName }),
        };
        const attendeeRes = await axios.post('/api/attendees', payload);
        const newAttendee = attendeeRes.data;
        if (newAttendee.error) {
            enqueueSnackbar('Attendee record already exists');
            return null;
        }
        return { attendee: newAttendee, registration, insuranceCost };
    } catch (e) {
        console.error('Failed to create attendee:', e);
        enqueueSnackbar('Registration failed: ' + (e.response?.data?.error || e.message));
        return null;
    }
}

    useImperativeHandle(ref, () => ({ submit, canSubmit }));

    const visibleFields = FIELD_CONFIG.filter(f => activeFields.includes(f.id));

    const fieldRows = visibleFields.reduce((rows, field, i) => {
        if (i % 2 === 0) rows.push([field]);
        else rows[rows.length - 1].push(field);
        return rows;
    }, []);

    const isFieldMissing = (fieldId) => {
    const config = FIELD_CONFIG.find(f => f.id === fieldId);
    if (!config?.required) return false; // optional fields are never "missing"
    const val = attendee[fieldId];
    return String(val ?? '').trim() === '' && activeFields.includes(fieldId);
};

    // Only show validation styling once the user has touched the field or
    // attempted to submit.
    const shouldShowError = (fieldId) =>
        (touched[fieldId] || submitAttempted) && isFieldMissing(fieldId);

    const shouldShowValid = (fieldId) =>
        touched[fieldId] && !isFieldMissing(fieldId) && String(attendee[fieldId] ?? '').trim() !== '';

    const fieldClass = (fieldId) => {
        if (shouldShowError(fieldId)) return 'form-control is-invalid';
        if (shouldShowValid(fieldId)) return 'form-control is-valid';
        return 'form-control';
    };

    return (
        <div className="registrationForms mb-4">
            <div className="full-row gray px-4 py-3 mb-4 rounded">
                <div className="row align-items-center g-2">
                    <div className="col">
                        <h5 className="mb-0 text-uppercase">
                            {registration.RegistrationName}
                            {quantity > 1 && (
                                <span className="ms-2 fs-6 fw-normal text-muted">
                                    — Attendee {formIndex + 1} of {quantity}
                                </span>
                            )}
                        </h5>
                    </div>
                    {registration.RegistrationCost > 0 && (
                        <div className="col-auto">
                            <span className="badge bg-white text-dark fs-6">
                                {formatMoney(registration.RegistrationCost)}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            <div className="px-4 pb-3">
                {fieldRows.map((row, rowIndex) => (
                    <div className="row mb-3" key={rowIndex}>
                        {row.map((field) => (
                            <div className="col-md-6 col-12" key={field.id}>
                                <div className="form-group">
                                    <label
                                        htmlFor={`${field.id}-${formIndex}`}
                                        className="control-label editing-form-label"
                                    >
                                    {field.label}
                                    {field.required && <span className="text-danger ms-1">*</span>}
                                    </label>
                                    {field.id === 'BPAttendeePhone' ? (
                                        <InputMask
                                            mask="(999) 999-9999"
                                            value={attendee.BPAttendeePhone || ''}
                                            onChange={(e) => {
                                                handleAttendeeInputChange({
                                                    target: { id: field.id, value: e.target.value }
                                                });
                                            }}
                                            onBlur={() => handleBlur(field.id)}
                                        >
                                            {(inputProps) => (
                                                <input
                                                    {...inputProps}
                                                    id={`${field.id}-${formIndex}`}
                                                    name={field.id}
                                                    type="text"
                                                    inputMode="numeric"
                                                    autoComplete="tel"
                                                    className={fieldClass(field.id)}
                                                    placeholder={field.placeholder}
                                                />
                                            )}
                                        </InputMask>
                                    ) : (
                                        <input
                                            id={`${field.id}-${formIndex}`}
                                            name={field.id}
                                            type={field.type}
                                            className={fieldClass(field.id)}
                                            value={attendee[field.id]}
                                            onChange={(e) => {
                                                handleAttendeeInputChange({
                                                    target: { id: field.id, value: e.target.value }
                                                });
                                            }}
                                            onBlur={() => handleBlur(field.id)}
                                            placeholder={field.placeholder}
                                        />
                                    )}
                                    {shouldShowError(field.id) && (
                                        <div className="invalid-feedback d-block">{field.label} is required</div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {row.length === 1 && <div className="col-md-6 col-12 d-none d-md-block" />}
                    </div>
                ))}

                {visibleFields.length > 0 && (
                    <div className="row mt-2">
                        <div className="col-12">
                            {canSubmit ? (
                                <p className="text-success mb-0">
                                    <i className="fas fa-check-circle me-1" />All required fields are complete
                                </p>
                            ) : (
                                <p className="text-muted mb-0">
                                    <i className="fas fa-info-circle me-1" />Please fill in all required fields
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
});

export default AttendeeForm;